import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyProposals, completeProposal, deleteProposal } from '../services/api';

const MyProposals = () => {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    try {
      const response = await getMyProposals();
      setProposals(response.data);
    } catch (err) {
      setError('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (proposalId, success) => {
    try {
      await completeProposal(proposalId, success);
      loadProposals();
    } catch (error) {
      console.error('Error completing proposal:', error);
    }
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa proposta?')) return;

    try {
      await deleteProposal(proposalId);
      loadProposals();
    } catch (error) {
      console.error('Error deleting proposal:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatAvailability = (proposal) => {
    if (proposal.selectedAvailability !== null && proposal.availabilities[proposal.selectedAvailability]) {
      const av = proposal.availabilities[proposal.selectedAvailability];
      return `${formatDate(av.date)} ${av.timeSlot}`;
    }
    return proposal.availabilities.map(av =>
      `${formatDate(av.date)} ${av.timeSlot}${av.targetConfirmed ? ' (confermato)' : ''}`
    ).join(', ');
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'open': return 'In attesa';
      case 'confirmed': return 'Confermata';
      case 'completed': return 'Completata';
      case 'cancelled': return 'Annullata';
      default: return status;
    }
  };

  const isOrganizer = (proposal) =>
    proposal.organizerId._id === user._id || proposal.organizerId === user._id;

  if (loading) return <div className="loading">Caricamento...</div>;
  if (error) return <div className="error-message">{error}</div>;

  const organized = proposals.filter(p => isOrganizer(p));
  const participating = proposals.filter(p => !isOrganizer(p));

  return (
    <div className="page-container">
      <div className="page-header-flex">
        <h1>Le mie proposte</h1>
        <Link to="/proposals/new" className="btn btn-primary">
          Nuova proposta
        </Link>
      </div>

      <section className="section">
        <h2>Proposte organizzate da me</h2>
        {organized.length === 0 ? (
          <p className="empty-state">Non hai organizzato proposte</p>
        ) : (
          <div className="proposal-list">
            {organized.map((proposal) => (
              <div key={proposal._id} className={`proposal-card proposal-${proposal.status}`}>
                <div className="proposal-header">
                  <span className={`badge badge-${proposal.status}`}>
                    {getStatusText(proposal.status)}
                  </span>
                  <Link to={`/targets/${proposal.targetId._id}`} className="proposal-target">
                    {proposal.targetId.name}
                  </Link>
                </div>

                <div className="proposal-info">
                  <p><strong>Disponibilita:</strong> {formatAvailability(proposal)}</p>
                  {proposal.companionId && (
                    <p><strong>Accompagnatore:</strong> {proposal.companionId.username}</p>
                  )}
                  {proposal.status === 'completed' && (
                    <p>
                      <strong>Esito:</strong>{' '}
                      {proposal.completedSuccessfully ? 'Visita effettuata' : 'Non effettuata'}
                    </p>
                  )}
                </div>

                <div className="proposal-actions">
                  {proposal.status === 'confirmed' && (
                    <>
                      <button
                        onClick={() => handleComplete(proposal._id, true)}
                        className="btn btn-success btn-small"
                      >
                        Visita effettuata
                      </button>
                      <button
                        onClick={() => handleComplete(proposal._id, false)}
                        className="btn btn-danger btn-small"
                      >
                        Non effettuata
                      </button>
                    </>
                  )}
                  {proposal.status === 'open' && (
                    <button
                      onClick={() => handleDelete(proposal._id)}
                      className="btn btn-danger btn-small"
                    >
                      Elimina
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <h2>Proposte a cui partecipo</h2>
        {participating.length === 0 ? (
          <p className="empty-state">Non partecipi a nessuna proposta</p>
        ) : (
          <div className="proposal-list">
            {participating.map((proposal) => (
              <div key={proposal._id} className={`proposal-card proposal-${proposal.status}`}>
                <div className="proposal-header">
                  <span className={`badge badge-${proposal.status}`}>
                    {getStatusText(proposal.status)}
                  </span>
                  <Link to={`/targets/${proposal.targetId._id}`} className="proposal-target">
                    {proposal.targetId.name}
                  </Link>
                </div>

                <div className="proposal-info">
                  <p><strong>Organizzatore:</strong> {proposal.organizerId.username}</p>
                  <p><strong>Data:</strong> {formatAvailability(proposal)}</p>
                  {proposal.status === 'completed' && (
                    <p>
                      <strong>Esito:</strong>{' '}
                      {proposal.completedSuccessfully ? 'Visita effettuata' : 'Non effettuata'}
                    </p>
                  )}
                </div>

                <div className="proposal-actions">
                  {proposal.status === 'confirmed' && (
                    <>
                      <button
                        onClick={() => handleComplete(proposal._id, true)}
                        className="btn btn-success btn-small"
                      >
                        Visita effettuata
                      </button>
                      <button
                        onClick={() => handleComplete(proposal._id, false)}
                        className="btn btn-danger btn-small"
                      >
                        Non effettuata
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MyProposals;
